import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import api from '../services/api';

interface SearchSettings {
  // AI Assistant settings
  similarityThreshold: number;
  maxResults: number;
  showSimilarityScore: boolean;
  autoClearHistory: boolean;
  
  // Find in Document settings
  findSimilarityThreshold: number;
  findShowSimilarityScore: boolean;
  
  // Help and tooltip settings
  helpMode: boolean;
  shortTooltips: boolean;
}

interface SearchSettingsContextType {
  settings: SearchSettings;
  updateSettings: (newSettings: Partial<SearchSettings>) => void;
  loadSettingsFromBackend: () => Promise<void>;
}

const defaultSettings: SearchSettings = {
  similarityThreshold: 0.7,
  maxResults: 5,
  showSimilarityScore: true,
  autoClearHistory: false,
  findSimilarityThreshold: 0.3,
  findShowSimilarityScore: true,
  helpMode: false,
  shortTooltips: true
};

export const SearchSettingsContext = createContext<SearchSettingsContextType | undefined>(undefined);

export const SearchSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SearchSettings>(defaultSettings);

  const updateSettings = useCallback((newSettings: Partial<SearchSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  const loadSettingsFromBackend = useCallback(async () => {
    try {
      const response = await api.get('/search/settings');
      const backendSettings = response.data.search;
      setSettings(prev => ({
        ...prev,
        ...backendSettings
      }));
    } catch (error) {
      console.error('Error loading search settings from backend:', error);
      // Keep using default settings if backend call fails
    }
  }, []);

  // Load settings from backend on mount
  useEffect(() => {
    loadSettingsFromBackend();
  }, [loadSettingsFromBackend]);

  return (
    <SearchSettingsContext.Provider value={{ settings, updateSettings, loadSettingsFromBackend }}>
      {children}
    </SearchSettingsContext.Provider>
  );
};

export const useSearchSettings = () => {
  const context = useContext(SearchSettingsContext);
  if (context === undefined) {
    throw new Error('useSearchSettings must be used within a SearchSettingsProvider');
  }
  return context;
}; 